import { type FetchLike, resolveFetch } from 'ProdantixSdk/core/http';
import { ReplayChunker } from 'ProdantixSdk/web/replay/chunker';
import { type MaskOptions, maskInputValue, maskText } from 'ProdantixSdk/web/replay/mask';
import { IdAllocator, NODE_ELEMENT, NODE_TEXT, type NodeLike, serializeNode } from 'ProdantixSdk/web/replay/serialize';
import { EVENT_FULL_SNAPSHOT, EVENT_INCREMENTAL, type ReplayChunk } from 'ProdantixSdk/web/replay/types';
import { uploadChunk } from 'ProdantixSdk/web/replay/upload';

const INPUT_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA']);

export interface RecorderOptions {
  apiKey: string;
  distinctId: () => string;
  fetch?: FetchLike;
  flushIntervalMs: number;
  mask: MaskOptions;
  maxEvents: number;
  now: () => number;
  onError?: (error: unknown) => void;
  replayHost: string;
  sessionId: string;
}

/** The browser session recorder (WI-6). Captures a full DOM snapshot, then streams
 *  incremental mutations + input/scroll interactions (input values masked), batching
 *  them into chunks the recorder service ingests. A node→id mirror built during the
 *  snapshot keeps mutation references stable. Best-effort: any capture/upload error is
 *  routed to `onError`, never thrown into the host page. */
export class SessionRecorder {
  private readonly allocator = new IdAllocator();
  private readonly mirror = new Map<Node, number>();
  private readonly chunker: ReplayChunker;
  private readonly fetchImpl?: FetchLike;
  private observer?: MutationObserver;
  private timer?: ReturnType<typeof setInterval>;
  private started = false;

  constructor(private readonly options: RecorderOptions) {
    this.chunker = new ReplayChunker({
      distinctId: options.distinctId,
      masked: options.mask.maskAllInputs,
      maxEvents: options.maxEvents,
      now: options.now,
      sessionId: options.sessionId,
    });
    this.fetchImpl = resolveFetch(options.fetch);
  }

  /** Begins recording. No-op when already started or when the DOM/fetch is unavailable
   *  (e.g. SSR) — session replay is a browser-only enhancement. */
  start(): void {
    if (this.started) {
      return;
    }
    const doc = getDocument();
    const root = doc?.documentElement;
    if (!doc || !root || !this.fetchImpl) {
      return;
    }
    this.started = true;
    this.captureFullSnapshot(root);
    this.observeMutations(root);
    this.observeInteractions(doc);
    this.timer = setInterval(() => this.upload(this.chunker.flush()), this.options.flushIntervalMs);
  }

  /** Stops recording and flushes any buffered events. */
  stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;
    this.observer?.disconnect();
    if (this.timer) {
      clearInterval(this.timer);
    }
    this.upload(this.chunker.flush());
  }

  private captureFullSnapshot(root: Element): void {
    const snapshot = serializeNode(this.adapt(root), this.allocator, this.options.mask, (node, id) => {
      const ref = node.ref;
      if (ref) {
        this.mirror.set(ref as Node, id);
      }
    });
    if (snapshot) {
      this.push(EVENT_FULL_SNAPSHOT, { node: snapshot });
    }
  }

  private observeMutations(root: Element): void {
    const Observer = (globalThis as { MutationObserver?: typeof MutationObserver }).MutationObserver;
    if (!Observer) {
      return;
    }
    this.observer = new Observer(records => {
      for (const record of records) {
        this.handleMutation(record);
      }
    });
    this.observer.observe(root, {
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  private handleMutation(record: MutationRecord): void {
    if (record.type === 'characterData') {
      const id = this.mirror.get(record.target);
      if (id !== undefined) {
        this.push(EVENT_INCREMENTAL, {
          texts: [{ id, value: maskText(record.target.textContent ?? '', this.options.mask) }],
        });
      }
      return;
    }
    if (record.type === 'attributes' && record.attributeName) {
      const id = this.mirror.get(record.target);
      const target = record.target as Element;
      if (id !== undefined) {
        this.push(EVENT_INCREMENTAL, {
          attributes: [{ attributes: { [record.attributeName]: target.getAttribute(record.attributeName) ?? '' }, id }],
        });
      }
      return;
    }
    if (record.type === 'childList') {
      const parentId = this.mirror.get(record.target);
      if (parentId === undefined) {
        return;
      }
      for (const added of Array.from(record.addedNodes)) {
        const serialized = serializeNode(this.adapt(added), this.allocator, this.options.mask, (node, id) => {
          const ref = node.ref;
          if (ref) {
            this.mirror.set(ref as Node, id);
          }
        });
        if (serialized) {
          this.push(EVENT_INCREMENTAL, { adds: [{ node: serialized, parentId }] });
        }
      }
      for (const removed of Array.from(record.removedNodes)) {
        const id = this.mirror.get(removed);
        if (id !== undefined) {
          this.push(EVENT_INCREMENTAL, { removes: [{ id, parentId }] });
          this.mirror.delete(removed);
        }
      }
    }
  }

  private observeInteractions(doc: Document): void {
    doc.addEventListener(
      'input',
      event => {
        const target = event.target as HTMLInputElement | null;
        if (!target || !INPUT_TAGS.has(target.tagName)) {
          return;
        }
        const id = this.mirror.get(target);
        if (id !== undefined) {
          this.push(EVENT_INCREMENTAL, {
            input: { id, value: maskInputValue(target.value ?? '', target.type, this.options.mask) },
          });
        }
      },
      true,
    );
    doc.addEventListener(
      'scroll',
      event => {
        const target = event.target as Element | null;
        const id = target ? this.mirror.get(target) : undefined;
        this.push(EVENT_INCREMENTAL, {
          scroll: { id: id ?? 0, x: target?.scrollLeft ?? 0, y: target?.scrollTop ?? 0 },
        });
      },
      true,
    );
  }

  private adapt(node: Node): NodeLike {
    const adapted: NodeLike = {
      childNodes: [],
      nodeType: node.nodeType,
      ref: node,
    };
    if (node.nodeType === NODE_TEXT) {
      adapted.textValue = node.textContent ?? '';
      return adapted;
    }
    if (node.nodeType === NODE_ELEMENT) {
      const element = node as Element;
      adapted.tagName = element.tagName;
      adapted.attributes = Array.from(element.attributes).map(attr => ({
        name: attr.name,
        value: attr.value,
      }));
      if (INPUT_TAGS.has(element.tagName)) {
        const input = element as HTMLInputElement;
        adapted.inputType = input.type;
        adapted.inputValue = input.value ?? '';
      }
    }
    adapted.childNodes = Array.from(node.childNodes).map(child => this.adapt(child));
    return adapted;
  }

  private push(type: number, data: unknown): void {
    const chunk = this.chunker.add({ data, timestamp: this.options.now(), type });
    if (chunk) {
      this.upload(chunk);
    }
  }

  private upload(chunk: ReplayChunk | null): void {
    if (!chunk || !this.fetchImpl) {
      return;
    }
    uploadChunk(chunk, {
      apiKey: this.options.apiKey,
      fetch: this.fetchImpl,
      replayHost: this.options.replayHost,
    }).catch(error => this.options.onError?.(error));
  }
}

function getDocument(): Document | undefined {
  return (globalThis as { document?: Document }).document;
}
